/* eslint-disable react/no-danger */
import React, { Children, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { Container, Section } from 'components/atoms/Page';
import { CoreBreadcrumbs } from 'components/core/library/breadcrumbs';
import { Crumb } from 'components/molecules';

import './BreadCrumb.scss';
import { normalizeSpaces } from 'utils/stringUtils';

function createSGPCrumbSchema(crumbs = [], baseUrl) {
	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: crumbs
			.filter((crumb) => crumb.props.label)
			.map((crumb, index) => ({
				'@type': 'ListItem',
				position: index + 1,
				name:
					normalizeSpaces(crumb.props.name) ||
					normalizeSpaces(crumb.props.label),
				item: `${baseUrl}${get(crumb, 'props.href', '').split('?').shift()}`,
			})),
	};
}

function createPSPCrumbSchema(crumbs = []) {
	return {
		'@context': 'http://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: crumbs
			.filter((crumb) => crumb.props.label)
			.map((crumb, index) => ({
				'@type': 'ListItem',
				position: index + 1,
				item: {
					'@id': get(crumb, 'props.href', '').split('?').shift(),
					name: crumb.props.name || crumb.props.label,
				},
			})),
	};
}

function createCrumbSchema(crumbs = [], baseUrl, pageType) {
	if (pageType === 'sgp') {
		return createSGPCrumbSchema(crumbs, baseUrl);
	}

	return createPSPCrumbSchema(crumbs);
}

export function BreadCrumbs({ crumbs, crumbJSON, background, pageType }) {
	return [
		<Section
			key="breadcrumb-section"
			className={`breadcrumb-section ${
				pageType === 'psp' ? 'psp-breadcrumb' : background
			}`}
		>
			<Container>
				<CoreBreadcrumbs key="crumbs">
					{Children.toArray(crumbs)}
				</CoreBreadcrumbs>
				{pageType !== 'psp' && (
					<script
						key="schema"
						type="application/ld+json"
						dangerouslySetInnerHTML={{
							__html: JSON.stringify(crumbJSON),
						}}
					/>
				)}
			</Container>
		</Section>,
	];
}

BreadCrumbs.propTypes = {
	crumbs: PropTypes.node,
	crumbJSON: PropTypes.object,
	pageType: PropTypes.string,
	background: PropTypes.string,
};

function EnhancedBreadCrumbs({
	location = {},
	cityUrl,
	stateUrl,
	crumbs: extraCrumbs = [],
	pageType,
	...rest
}) {
	const baseUrl = useSelector((state) => state.app.baseUrl);

	const { fullState, subLocality, city } = location;
	const sub = subLocality || city;

	const newCrumbs = useMemo(() => {
		const result = [];
		if (stateUrl) {
			result.push(
				<Crumb
					key="state"
					href={stateUrl}
					isLast={!cityUrl && !extraCrumbs.length}
					label={fullState}
					className="state"
				/>,
			);
		}
		if (cityUrl) {
			result.push(
				<Crumb
					key="sublocality"
					className="sublocality"
					href={cityUrl}
					isLast={!extraCrumbs.length}
					label={`${sub} self-storage`}
				>
					{sub} <span className="self-storage">self-storage</span>
				</Crumb>,
			);
		}
		return result;
	}, [stateUrl, cityUrl, extraCrumbs.length, fullState, sub]);

	const crumbs = useMemo(
		() => [...newCrumbs, ...extraCrumbs],
		[newCrumbs, extraCrumbs],
	);
	const crumbJSON = useMemo(
		() => createCrumbSchema(crumbs, baseUrl, pageType),
		[crumbs, baseUrl, pageType],
	);

	return (
		<BreadCrumbs
			crumbs={crumbs}
			crumbJSON={crumbJSON}
			pageType={pageType}
			{...rest}
		/>
	);
}

EnhancedBreadCrumbs.propTypes = {
	location: PropTypes.shape({
		fullState: PropTypes.string,
		subLocality: PropTypes.string,
		city: PropTypes.string,
	}),
	cityUrl: PropTypes.string,
	stateUrl: PropTypes.string,
	crumbs: PropTypes.arrayOf(PropTypes.node),
	pageType: PropTypes.string,
};

export default EnhancedBreadCrumbs;
